// Página Profile - Perfil del usuario actual
// Permite a los usuarios editar sus propios datos

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { getUser, updateUser } from '../services/users.service'
import { formatDate } from '../utils/helpers'
import Button from '../components/Button'
import Toast from '../components/Toast'
import './Profile.css'

/**
 * Página Profile
 * Permite a los usuarios editar sus propios datos
 */
function Profile() {
  const { currentUser, userData, loadUserData } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  // Estado del formulario
  const [formData, setFormData] = useState({
    displayName: '',
    phone: '',
    phoneAlternate: '',
    addresses: [],
    primaryAddressIndex: null,
    notes: ''
  })

  // Estado para nueva dirección
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    province: ''
  })

  const fetchUserData = useCallback(async () => {
    if (!currentUser?.uid) return
    try {
      setLoading(true)
      const user = await getUser(currentUser.uid)
      
      if (user) {
        setFormData({
          displayName: user.displayName || '',
          phone: user.phone || '',
          phoneAlternate: user.phoneAlternate || '',
          addresses: user.addresses || [],
          primaryAddressIndex: user.primaryAddressIndex !== undefined ? user.primaryAddressIndex : null,
          notes: user.notes || ''
        })
      }
    } catch (error) {
      console.error('Error cargando datos del usuario:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos del usuario'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  // Cargar datos del usuario
  useEffect(() => {
    if (currentUser) {
      fetchUserData()
    }
  }, [currentUser, fetchUserData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    setSaving(true)

    try {
      await updateUser(currentUser.uid, formData)
      
      // Recargar datos del usuario en el contexto
      await loadUserData(currentUser.uid)
      
      setNotification({
        type: 'success',
        message: 'Perfil actualizado correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error actualizando perfil:', error)
      setNotification({
        type: 'error',
        message: 'Error al actualizar el perfil'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const addAddress = () => {
    if (newAddress.street && newAddress.city && newAddress.province) {
      const newAddresses = [...formData.addresses, { ...newAddress }]
      setFormData(prev => ({
        ...prev,
        addresses: newAddresses,
        primaryAddressIndex: prev.primaryAddressIndex === null ? 0 : prev.primaryAddressIndex
      }))
      setNewAddress({
        street: '',
        city: '',
        postalCode: '',
        province: ''
      })
    }
  }

  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index)
    let newPrimaryIndex = formData.primaryAddressIndex
    
    if (formData.primaryAddressIndex === index) {
      newPrimaryIndex = newAddresses.length > 0 ? 0 : null
    } else if (formData.primaryAddressIndex > index) {
      newPrimaryIndex = formData.primaryAddressIndex - 1
    }
    
    setFormData(prev => ({
      ...prev,
      addresses: newAddresses,
      primaryAddressIndex: newPrimaryIndex
    }))
  }

  const setPrimaryAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      primaryAddressIndex: index
    }))
  }

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-state">
          <p>Cargando perfil...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="profile-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="profile-header">
        <h1>Mi Perfil</h1>
        <p className="profile-subtitle">
          Gestiona tu información personal y de contacto
        </p>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {/* Información básica */}
        <div className="form-section">
          <h2 className="section-title">Información Básica</h2>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={currentUser?.email || ''}
              disabled
              className="disabled-input"
            />
            <small className="form-hint">El email no se puede modificar</small>
          </div>

          <div className="form-group">
            <label htmlFor="displayName">Nombre Completo</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Tu nombre completo"
            />
          </div>
        </div>

        {/* Información de contacto */}
        <div className="form-section">
          <h2 className="section-title">Información de Contacto</h2>
          
          <div className="form-group">
            <label htmlFor="phone">Teléfono Principal</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+54 9 11 1234-5678"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneAlternate">Teléfono Alternativo</label>
            <input
              type="tel"
              id="phoneAlternate"
              name="phoneAlternate"
              value={formData.phoneAlternate}
              onChange={handleChange}
              placeholder="+54 9 11 1234-5678"
            />
          </div>
        </div>

        {/* Direcciones */}
        <div className="form-section">
          <h2 className="section-title">Direcciones</h2>
          
          {formData.addresses.length > 0 && (
            <div className="addresses-list">
              {formData.addresses.map((addr, index) => (
                <div key={index} className="address-item">
                  <div className="address-content">
                    <span className="address-text">
                      {addr.street}, {addr.city}, {addr.province} {addr.postalCode && `(${addr.postalCode})`}
                    </span>
                    {formData.primaryAddressIndex === index && (
                      <span className="primary-badge">Principal</span>
                    )}
                  </div>
                  <div className="address-actions">
                    {formData.primaryAddressIndex !== index && (
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setPrimaryAddress(index)}
                      >
                        Marcar como principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-link btn-danger"
                      onClick={() => removeAddress(index)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="new-address-form">
            <h3 className="subsection-title">Agregar Nueva Dirección</h3>
            <div className="form-row">
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Calle y número"
                  value={newAddress.street}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                />
              </div>
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Ciudad"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Código Postal"
                  value={newAddress.postalCode}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                />
              </div>
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Provincia"
                  value={newAddress.province}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, province: e.target.value }))}
                />
              </div>
            </div>
            <button
              type="button"
              className="btn-add-address"
              onClick={addAddress}
            >
              ➕ Agregar Dirección
            </button>
          </div>
        </div>

        {/* Notas */}
        <div className="form-section">
          <h2 className="section-title">Notas Personales</h2>
          <div className="form-group">
            <label htmlFor="notes">Notas / Observaciones</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas personales..."
              rows={4}
            />
          </div>
        </div>

        {/* Información del sistema (solo lectura) */}
        {userData && (
          <div className="form-section">
            <h2 className="section-title">Información del Sistema</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Rol:</label>
                <span>{userData.role === 'admin' ? 'Administrador' : userData.role === 'superadmin' ? 'Super Admin' : 'Usuario'}</span>
              </div>
              <div className="info-item">
                <label>Fecha de Registro:</label>
                <span>
                  {userData.createdAt 
                    ? (userData.createdAt.toDate 
                        ? formatDate(userData.createdAt.toDate())
                        : formatDate(new Date(userData.createdAt)))
                    : 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <label>Email Verificado:</label>
                <span>{currentUser?.emailVerified ? 'Sí' : 'No'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default Profile

